import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ShieldCheck, Download, Zap } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import { Button } from "../components/ui/button";
import { useStore } from "../context/StoreContext";
import FramedProductImage from "../components/FramedProductImage";
import AuctionPanel from "../components/AuctionPanel";
import { resolveAdjust } from "../components/ProductCard";
import api, { money } from "../lib/api";

export default function ProductPage() {
  const { slug } = useParams();
  const { addToCart, bootstrap, setBuyNow } = useStore();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variation, setVariation] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [auction, setAuction] = useState(null);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setAuction(null);
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setVariation(r.data.variations?.[0] || null);
        setActiveImage(0);
        setQuantity(1);
        api.get(`/auctions/by-product/${r.data.id}`)
          .then((res) => setAuction(res.data?.auction || null))
          .catch(() => {});
        if (r.data.meta_title) document.title = r.data.meta_title;
        if (r.data.meta_description) {
          let tag = document.querySelector('meta[name="description"]');
          if (!tag) {
            tag = document.createElement("meta");
            tag.name = "description";
            document.head.appendChild(tag);
          }
          tag.content = r.data.meta_description;
        }
      })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
  }, [slug]);

  const images = useMemo(() => {
    if (!product) return [];
    const varImgs = variation?.images || [];
    return varImgs.length ? varImgs : product.images || [];
  }, [product, variation]);

  const pa = product?.price_adjust || null;
  const st = bootstrap?.settings;

  if (notFound) {
    return (
      <div><Header />
        <main className="max-w-7xl mx-auto px-6 py-32 text-center text-zinc-500" data-testid="product-not-found">Produto não encontrado.</main>
        <Footer />
      </div>
    );
  }
  if (!product) return <div className="min-h-screen bg-background" data-testid="product-loading" />;

  const price = variation ? (variation.promo_price || variation.price) : 0;
  const basePrice = variation?.price || 0;
  const inStock = product.type === "digital" || (variation && (variation.stock || 0) > 0);

  const handleAdd = () => {
    if (!variation) return;
    addToCart({
      product_id: product.id,
      variation_id: variation.id,
      quantity,
      name: product.name,
      variation_name: variation.name,
      sku: variation.sku,
      price,
      base_price: variation.price,
      image: images[0] || null,
      slug: product.slug,
      type: product.type,
    });
    toast.success("Adicionado ao carrinho");
  };

  const handleBuyNow = () => {
    if (!variation) return;
    setBuyNow({
      product_id: product.id,
      variation_id: variation.id,
      quantity,
      name: product.name,
      variation_name: variation.name,
      sku: variation.sku,
      price,
      base_price: variation.price,
      image: images[0] || null,
      slug: product.slug,
      type: product.type,
    });
    navigate("/checkout");
  };

  return (
    <div data-testid="product-page">
      <Header />
      <CartDrawer />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        <nav className="text-xs text-zinc-500 mb-10 flex gap-2" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-gold transition-colors">Início</Link>
          <span>/</span>
          {product.category && (
            <>
              <Link to={`/categoria/${product.category.slug}`} className="hover:text-gold transition-colors">{product.category.name}</Link>
              <span>/</span>
            </>
          )}
          <span className="text-zinc-300">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <FramedProductImage
              image={images[activeImage]}
              name={product.name}
              price={money(price)}
              adjust={resolveAdjust(product, bootstrap?.settings)}
              priceDy={pa ? (pa.dy || 0) : (st?.card_price_dy || 0)}
              priceDx={pa ? (pa.dx || 0) : (st?.card_price_dx || 0)}
              priceScale={pa ? (pa.scale || 1) : (st?.card_price_scale || 1)}
              priceColor={pa ? (pa.color || "#241600") : (st?.card_price_color || "#241600")}
              priceWidth={pa ? (pa.width || 52) : (st?.card_price_width || 52)}
              priceHeight={pa ? (pa.height || 6) : (st?.card_price_height || 6)}
              priceRadius={pa ? (pa.radius ?? 2) : (st?.card_price_radius ?? 2)}
              hoverZoom={false}
              testid="product-main-image"
            />
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImage(i)} data-testid={`product-thumb-${i}`}
                    className={`w-20 h-20 rounded-md overflow-hidden border transition-colors ${i === activeImage ? "border-gold" : "border-white/10 hover:border-white/30"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:py-6">
            {product.type === "digital" && (
              <p className="label-caps mb-3 flex items-center gap-2"><Download size={13} /> Entrega digital imediata</p>
            )}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-tight" data-testid="product-name">{product.name}</h1>

            {auction ? (
              <div className="mt-8" data-testid="product-auction">
                <p className="text-zinc-300 leading-relaxed mb-6" data-testid="product-description">{product.description}</p>
                <AuctionPanel auction={auction} />
              </div>
            ) : (
              <>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-3xl text-gold" data-testid="product-price">{money(price)}</span>
              {variation?.promo_price && variation.promo_price < basePrice && (
                <span className="text-zinc-500 line-through" data-testid="product-base-price">{money(basePrice)}</span>
              )}
            </div>

            <p className="mt-6 text-zinc-300 leading-relaxed" data-testid="product-description">{product.description}</p>

            {product.variations?.length > 0 && (
              <div className="mt-8">
                <p className="label-caps mb-3">Variação</p>
                <div className="flex flex-wrap gap-3" data-testid="variation-selector">
                  {product.variations.map((v) => (
                    <button
                      key={v.id}
                      data-testid={`variation-${v.sku || v.id}`}
                      onClick={() => { setVariation(v); setActiveImage(0); }}
                      className={`px-4 py-2.5 border rounded-md text-sm transition-colors ${
                        variation?.id === v.id
                          ? "border-gold text-gold bg-amber-500/10"
                          : "border-white/15 text-zinc-300 hover:border-white/40"
                      } ${product.type === "physical" && (v.stock || 0) <= 0 ? "opacity-40" : ""}`}
                    >
                      {v.name}
                      {product.type === "physical" && (v.stock || 0) <= 0 && <span className="ml-1 text-xs">(esgotado)</span>}
                    </button>
                  ))}
                </div>
                {variation?.sku && <p className="text-xs text-zinc-600 mt-3">SKU: {variation.sku}</p>}
                {product.type === "physical" && variation && (
                  <p className="text-xs text-zinc-500 mt-1" data-testid="variation-stock">
                    {variation.stock > 0 ? `${variation.stock} em estoque` : "Sem estoque"}
                  </p>
                )}
              </div>
            )}

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-white/15 rounded-md">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:text-gold transition-colors" data-testid="qty-minus" aria-label="Diminuir"><Minus size={15} /></button>
                <span className="w-10 text-center text-sm" data-testid="qty-value">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(99, quantity + 1))} className="p-3 hover:text-gold transition-colors" data-testid="qty-plus" aria-label="Aumentar"><Plus size={15} /></button>
              </div>
              <Button
                data-testid="add-to-cart-button"
                disabled={!inStock}
                onClick={handleAdd}
                className="flex-1 bg-gold text-black hover:bg-gold-light font-semibold h-12 transition-colors disabled:opacity-40"
              >
                <ShoppingBag size={16} className="mr-2" />
                {inStock ? "Adicionar ao carrinho" : "Indisponível"}
              </Button>
            </div>

            <Button
              data-testid="buy-now-button"
              disabled={!inStock}
              onClick={handleBuyNow}
              variant="outline"
              className="w-full mt-3 border-gold/50 text-gold hover:bg-amber-500/10 font-semibold h-12 transition-colors disabled:opacity-40"
            >
              <Zap size={16} className="mr-2" />
              Comprar agora
            </Button>
              </>
            )}

            <div className="mt-10 flex items-center gap-3 text-xs text-zinc-500 border-t border-white/10 pt-6">
              <ShieldCheck size={16} className="text-gold shrink-0" />
              <span>Compra com código de autenticidade único e garantia verificável.</span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
